import { type InvalidUrlConnectionError, isInvalidUrlConnectionError } from '@nx/errors';
import type { Connection, Instance } from '@nx/state';
import {
  ACTION,
  MODAL_TYPE,
  StoreKit,
  login,
  useActiveProject,
  useCapability,
  useConnection,
  useModal,
  useModalClose,
  usePermissions,
  useUserId,
} from '@nx/state';
import * as StdLib from '@nx/stdlib';

import { CONNECTION_PARAMS } from '../connection-form-utils';
import type { ConnectionFormValues } from '../connection-form.types';

export const useConnect = () => {
  const connection = useConnection();
  const userId = useUserId();
  const connectInstanceModal = useModal(MODAL_TYPE.CONNECTION_FORM);
  const closeModal = useModalClose();
  const activeProject = useActiveProject(false);
  const { isAllowed } = usePermissions();
  const [seamlessEnabled] = useCapability('framework:seamless-connection');

  const closeModalAndCleanURL = () => {
    closeModal();
    StdLib.URLs.cleanSearchParamsAndHash([...CONNECTION_PARAMS]);
  };

  const connectSeamless = async (
    source: Connection['source'],
    url: string,
    name?: string,
    onError?: (e: InvalidUrlConnectionError) => void,
  ) => {
    const { globalPersistor } = StoreKit.UNSAFE_getGlobalStoreInstance();

    // The login should be moved to the seamless connection file once we have reworked the connection slice
    if (userId === null) {
      // Seamless is available but the user is not logged in the application.
      connection.provisionSso({
        url,
        name,
        credentials: { type: 'seamless' },
        source,
      });

      // Here we're ensuring that the connection data is saved to the store by calling flush before we redirect
      // to the SSO login page by calling login, otherwise the connection will fail.
      await globalPersistor.flush();

      // The modal is closed here so login will persist the background
      // URL instead of the URL to the connection form, before redirecting the user.
      closeModalAndCleanURL();

      void login();
      return;
    }

    // Seamless is available and the user is logged in.
    connection
      .connect({
        credentials: {
          type: 'seamless',
        },
        name,
        password: '',
        source,
        url,
      })
      .unwrap()
      .then(closeModalAndCleanURL)
      .catch((e: unknown) => {
        if (onError !== undefined && isInvalidUrlConnectionError(e)) {
          onError(e);
        }
      });
  };

  const connectBasicAuth = (
    params: {
      formValues: ConnectionFormValues;
      onError?: () => void;
      source: Connection['source'];
      isTransient?: Connection['isTransient'];
    },
    _onError?: never,
    _source?: never,
  ) => {
    const url = `${params.formValues.protocol}//${params.formValues.hostname}`;

    connection
      .connect({
        credentials: {
          type: 'basic',
          username: params.formValues.username,
        },
        name: params.formValues.name || undefined,
        password: params.formValues.password,
        source: params.source,
        isTransient: params.isTransient,
        url,
        dbName: params.formValues.dbName,
      })
      .unwrap()
      .then(closeModalAndCleanURL)
      .catch(() => {
        if (params.onError !== undefined) {
          params.onError();
        }
      });
  };

  // eslint-disable-next-line require-await, @typescript-eslint/require-await
  const doDiscoveryAndConnect = async (instance: Instance, trySeamless = true) => {
    const openConnectWithCredentials = (connectErrorMessage?: true | string) => {
      const warning =
        trySeamless && !instance.seamlessConnectionEnabled
          ? 'Please use a database user and password to connect.'
          : undefined;
      const url = StdLib.URLs.Neo4jURL.asNullable(instance.boltUrl);
      connectInstanceModal.open({
        instanceDetails: {
          id: instance.id,
          name: instance.name,
        },
        initialValues: {
          username: instance.isMultiTenantDb ? instance.id : 'neo4j',
          protocol: url?.protocol ?? StdLib.URLs.PROTOCOL_NEO4J_SECURE,
          hostname: url?.hostname ?? '',
        },
        source: { type: 'aura', sourceId: instance.id },
        error: connectErrorMessage,
        warning,
        hideConnectionMethods: false,
      });
    };
    let seamlessErrorString: boolean | string = true;
    if (seamlessEnabled) {
      const seamlessAllowed = isAllowed(
        ACTION.CREATE,
        `namespaces/${activeProject?.id}/databases/${instance.id}/tokens`,
      );
      seamlessErrorString = seamlessAllowed
        ? true
        : 'Your console role does not have permission to automatically connect to this instance.';
    }

    if (trySeamless && instance.seamlessConnectionEnabled) {
      void connectSeamless({ type: 'aura', sourceId: instance.id }, instance.boltUrl, instance.name, () => {
        void openConnectWithCredentials(seamlessErrorString);
      });
    } else {
      void openConnectWithCredentials(seamlessErrorString);
    }
  };

  return {
    connectSeamless,
    connectBasicAuth,
    doDiscoveryAndConnect,
  };
};
